import { useAppSelector } from "@/redux/hooks";
import { useSuggestedQueries } from "@brandclub/common-ui";
import { useTheme } from "@mui/material";
import React, { memo } from "react";
import { ConciergeWelcomeEmbedded } from "./ConciergeWelcomeEmbedded";
import { usePageContext } from "../hooks/usePageContext";

interface ConciergeWelcomeProps {
  trendingSearches?: Array<{ text: string }>;
  onSearch: (searchTerm: string) => void;
  brandId?: number;
  isEmbedded?: boolean;
}

const ConciergeWelcomeComponent = ({
  trendingSearches,
  onSearch,
  brandId,
  isEmbedded = false,
}: ConciergeWelcomeProps) => {
  const { data, isLoading: isLoadingSuggestedQueries } =
    useSuggestedQueries(brandId);

  const theme = useTheme();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const userName = userProfile?.given_name || "";

  // Memoize displayQueries to prevent recalculation on every render
  const displayQueries = React.useMemo(
    () =>
      data?.suggestedQuestions ||
      trendingSearches?.map((search) => search.text) ||
      [],
    [data?.suggestedQuestions, trendingSearches]
  );

  // Get page context (always call hook, but only use when embedded)
  const pageContext = usePageContext();

  // Custom welcome screen for embedded mode
  if (isEmbedded) {
    return (
      <ConciergeWelcomeEmbedded
        displayQueries={displayQueries}
        isLoadingSuggestedQueries={isLoadingSuggestedQueries}
        onSearch={onSearch}
        pageContext={pageContext}
      />
    );
  }

  // Full welcome screen for non-embedded mode
  return (
    <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center">
      {/* CONCIERGE label */}
      <p
        className="tw:font-bold tw:text-[16px] tw:leading-[32px] tw:tracking-[2px] tw:uppercase"
        style={{ color: theme.palette.text.primary }}
      >
        CONCIERGE
      </p>

      {/* Personalized heading */}
      <h1
        className="tw:font-semibold tw:text-[24px] tw:leading-[32px] tw:tracking-[0.08px] tw:mt-4"
        style={{ color: theme.palette.text.primary }}
      >
        Let us help you find what you've been looking for
        {userName ? `, ${userName}` : ""}.
      </h1>
    </div>
  );
};

// Memoize to prevent re-renders when parent re-renders due to input changes
const ConciergeWelcome = memo(ConciergeWelcomeComponent);

export default ConciergeWelcome;
